/**
 * GHALIA — promo rules (theme settings → bag + checkout alignment).
 * Configure in Theme settings → Cart drawer. Match the same values on the
 * Shopify Admin discount code (WELCOME): percent, min basket, max discount SAR.
 */
(() => {
  const P = () => window.GhaliaPricing;

  const rootConfig = () => window.Shopify?.promoConfig || {};

  const drawerOverrides = (drawer) => {
    if (!drawer) return {};
    return {
      minBasketSar: Number(drawer.dataset.promoMinSar || drawer.dataset.promoBasketCapSar || 0),
      maxDiscountSar: Number(drawer.dataset.promoMaxSar || 0),
      percent: Number(drawer.dataset.promoPercent || 0),
      defaultCode: String(drawer.dataset.promoDefaultCode || '').trim(),
    };
  };

  const read = (drawer) => {
    const root = rootConfig();
    const dom = drawerOverrides(drawer);
    return {
      percent: dom.percent > 0 ? dom.percent : Number(root.percent) || 10,
      minBasketSar: dom.minBasketSar > 0 ? dom.minBasketSar : Number(root.minBasketSar) || 750,
      maxDiscountSar: dom.maxDiscountSar > 0 ? dom.maxDiscountSar : Number(root.maxDiscountSar) || 75,
      defaultCode: dom.defaultCode || root.defaultCode || 'WELCOME',
      vatRate: Number(root.vatRate) || P()?.vatRate?.() || 15,
    };
  };

  const grossCentsFromSar = (sar) => Math.round(Number(sar) * 100);

  const netCentsFromGrossSar = (sar, vatRate) => {
    if (!sar) return 0;
    const rate = vatRate || 15;
    return Math.round((sar * 100 * 100) / (100 + rate));
  };

  const qualifyingGrossCents = (qualifyingNet) => {
    const pricing = P();
    return pricing?.displayCents?.(qualifyingNet) ?? qualifyingNet;
  };

  const meetsMinimum = (qualifyingNet, drawer) => {
    const cfg = read(drawer);
    const minGross = grossCentsFromSar(cfg.minBasketSar);
    if (minGross <= 0) return true;
    return qualifyingGrossCents(qualifyingNet) >= minGross;
  };

  const capNetSavings = (netCents, drawer) => {
    const cfg = read(drawer);
    const maxNet = netCentsFromGrossSar(cfg.maxDiscountSar, cfg.vatRate);
    if (maxNet > 0 && netCents > maxNet) return maxNet;
    return netCents;
  };

  const computeSavingsNet = (qualifyingNet, drawer) => {
    const cfg = read(drawer);
    if (qualifyingNet <= 0 || !meetsMinimum(qualifyingNet, drawer)) return 0;
    const pct = cfg.percent > 0 ? cfg.percent : 10;
    return capNetSavings(Math.round((qualifyingNet * pct) / 100), drawer);
  };

  /** Prefer Shopify allocation (checkout source of truth), fall back to theme rules. */
  const resolveSavingsNet = (cart, drawer, qualifyingNet) => {
    const pricing = P();
    let shopifyNet = 0;
    (cart?.items || []).forEach((item) => {
      if (pricing?.isNativeBundleLine?.(item) || pricing?.isFreeGiftLine?.(item)) return;
      shopifyNet += (item.line_level_discount_allocations || []).reduce(
        (sum, alloc) => sum + (Number(alloc.amount) || 0),
        0
      );
    });
    if (shopifyNet <= 0) shopifyNet = Number(cart?.total_discount) || 0;
    if (shopifyNet > 0) return capNetSavings(shopifyNet, drawer);
    return computeSavingsNet(qualifyingNet, drawer);
  };

  const formatAmount = (netCents, pricesIncludeVat) => {
    const pricing = P();
    if (!pricing || netCents <= 0) return '';
    return pricesIncludeVat ? pricing.formatNet(netCents) : pricing.formatDisplay(netCents);
  };

  window.GhaliaPromoConfig = {
    read,
    meetsMinimum,
    capNetSavings,
    computeSavingsNet,
    resolveSavingsNet,
    formatAmount,
    grossCentsFromSar,
    netCentsFromGrossSar,
  };
})();
